import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Mail, Save, Send, RefreshCw, Loader2, Eye, EyeOff, 
  CheckCircle, XCircle, ToggleLeft, ToggleRight, 
  Server, Phone, User, AtSign
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminEmailSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [settings, setSettings] = useState({
    enabled: false,
    from_name: 'GoTurkeyTransfer',
    from_email: 'info@goturkeytransfer.com',
    reply_to: 'info@goturkeytransfer.com',
    support_phone: '+905352385797',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_tls: true,
    logo_url: ''
  });

  const [testEmail, setTestEmail] = useState('');

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings`, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });

      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Fetch email settings error:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Email settings saved');
        fetchSettings();
      } else {
        toast.error(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings/test`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      setTestResult(data);
      
      if (data.success) {
        toast.success('SMTP connection successful');
      } else {
        toast.error(data.message || 'Connection failed');
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingTest(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/email-settings/test-send`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: testEmail })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Test email sent to ${testEmail}`);
      } else {
        toast.error(data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
        <AdminSidebar />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-3" data-testid="email-settings-title">
                <Mail className="h-6 w-6 text-primary" />
                Email Settings
              </h1>
              <p className="text-muted-foreground">
                Configure SMTP settings for sending booking emails
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleChange('enabled', !settings.enabled)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  settings.enabled 
                    ? 'bg-green-500/10 text-green-500' 
                    : 'bg-muted text-muted-foreground'
                }`}
                data-testid="toggle-email"
              >
                {settings.enabled ? (
                  <ToggleRight className="h-5 w-5" />
                ) : (
                  <ToggleLeft className="h-5 w-5" />
                )}
                {settings.enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          </div>

          {/* Sender Info */}
          <Card className="bg-secondary border-border mb-6">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <User className="h-5 w-5" />
                Sender Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    From Name
                  </label>
                  <Input
                    value={settings.from_name}
                    onChange={(e) => handleChange('from_name', e.target.value)}
                    placeholder="GoTurkeyTransfer"
                    className="bg-background"
                    data-testid="input-from-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    From Email
                  </label>
                  <Input
                    type="email"
                    value={settings.from_email}
                    onChange={(e) => handleChange('from_email', e.target.value)}
                    placeholder="info@goturkeytransfer.com"
                    className="bg-background"
                    data-testid="input-from-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Reply-To Email
                  </label>
                  <Input
                    type="email"
                    value={settings.reply_to}
                    onChange={(e) => handleChange('reply_to', e.target.value)}
                    placeholder="info@goturkeytransfer.com"
                    className="bg-background"
                    data-testid="input-reply-to"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Support Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={settings.support_phone}
                      onChange={(e) => handleChange('support_phone', e.target.value)}
                      placeholder="+905352385797"
                      className="bg-background pl-10"
                      data-testid="input-support-phone"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Logo URL (optional)
                </label>
                <Input
                  value={settings.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  placeholder="https://goturkeytransfer.com/logo.png"
                  className="bg-background"
                  data-testid="input-logo-url"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty to use text logo "GoTurkeyTransfer"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* SMTP Settings */}
          <Card className="bg-secondary border-border mb-6">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Server className="h-5 w-5" />
                SMTP Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    SMTP Host <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={settings.smtp_host}
                    onChange={(e) => handleChange('smtp_host', e.target.value)}
                    placeholder="smtp.titan.email"
                    className="bg-background"
                    data-testid="input-smtp-host"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    SMTP Port <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => handleChange('smtp_port', parseInt(e.target.value) || 587)}
                    placeholder="587"
                    className="bg-background"
                    data-testid="input-smtp-port"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    SMTP Username <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={settings.smtp_user}
                      onChange={(e) => handleChange('smtp_user', e.target.value)}
                      placeholder="info@goturkeytransfer.com"
                      className="bg-background pl-10"
                      data-testid="input-smtp-user"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    SMTP Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.smtp_password}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                      placeholder={settings.smtp_password === 'set' ? '••••••••' : 'Enter password'}
                      className="bg-background pr-10"
                      data-testid="input-smtp-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {settings.smtp_password === 'set' && (
                    <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Password is set (enter new value to change)
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.smtp_tls}
                    onChange={(e) => handleChange('smtp_tls', e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                    data-testid="checkbox-smtp-tls"
                  />
                  <span className="text-sm text-foreground">Use TLS/STARTTLS</span>
                </label>
              </div>
              
              {/* Test Result */}
              {testResult && (
                <div className={`p-4 rounded-lg flex items-center gap-3 ${
                  testResult.success 
                    ? 'bg-green-500/10 text-green-500' 
                    : 'bg-red-500/10 text-red-500'
                }`}>
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <XCircle className="h-5 w-5" />
                  )}
                  <span>{testResult.message}</span>
                </div>
              )}
              
              {/* Test Connection Button */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testing || !settings.smtp_host || !settings.smtp_user}
                  data-testid="test-connection-btn"
                >
                  {testing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Send Test Email */}
          <Card className="bg-secondary border-border mb-6">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Send className="h-5 w-5" />
                Send Test Email
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Send a test email to verify your SMTP configuration is working correctly.
              </p>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="bg-background flex-1"
                  data-testid="input-test-email"
                />
                <Button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest || !testEmail || !settings.enabled}
                  data-testid="send-test-btn"
                >
                  {sendingTest ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Test
                </Button>
              </div>
              {!settings.enabled && (
                <p className="text-xs text-yellow-500 mt-2">
                  Enable email service above to send test emails
                </p>
              )}
            </CardContent>
          </Card>

          {/* Email Types Info */}
          <Card className="bg-secondary border-border mb-6">
            <CardHeader>
              <CardTitle className="text-foreground">
                Email Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                The following emails are sent automatically when enabled:
              </p>
              <div className="space-y-3">
                {[
                  { name: 'Booking Confirmation', trigger: 'After successful booking creation' },
                  { name: 'Payment Received', trigger: 'After Stripe/PayPal payment success' },
                  { name: 'Cancellation Confirmation', trigger: 'After booking cancellation' },
                  { name: 'Reminder (24h before)', trigger: '24 hours before pickup time' }
                ].map((email) => (
                  <div key={email.name} className="flex items-center justify-between p-3 bg-background rounded-lg">
                    <div>
                      <h4 className="font-medium text-foreground">{email.name}</h4>
                      <p className="text-xs text-muted-foreground">{email.trigger}</p>
                    </div>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary"
              data-testid="save-email-settings-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEmailSettingsPage;
